import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Rocket,
  TrendingUp, 
  Users, 
  DollarSign, 
  ArrowRight, 
  CheckCircle2,
  Zap,
  Target,
  Clock,
  Building2,
  Laptop,
  Coins,
  Layers,
  GraduationCap,
  Briefcase,
  ChevronRight,
  Save,
  Sparkles,
  ArrowUpRight,
  BarChart3
} from 'lucide-react';

const ScaleValueModule = ({ onClose, onSavePlan }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  
  const [currentScreen, setCurrentScreen] = useState(0);
  const [userProfile, setUserProfile] = useState({
    incomeModel: null,
    industry: '',
    skills: '',
    interests: ''
  });
  const [scalingPlan, setScalingPlan] = useState({
    goal: '',
    strategy: null,
    actions: []
  });
  const [suggestedPaths, setSuggestedPaths] = useState([]);

  // Content translations
  const content = {
    en: {
      // Slide 1 - Title
      slide1: {
        title: "Scale Your Value",
        message: "If you create value and capture value, you can earn a living.",
        highlight: "If you learn to scale value, you build wealth.",
        button: "Start Learning",
        time: "8-10 min"
      },
      // Slide 2 - The Scaling Problem
      slide2: {
        title: "The Scaling Problem",
        intro: "Most people earn money by trading time for money:",
        formula: "Income = Time × Rate",
        problem: "This creates a fundamental limitation:",
        limitations: [
          "There are only 24 hours in a day",
          "Your energy and focus are limited",
          "Income has a ceiling tied to your hours"
        ],
        solution: "Scaling value means breaking this constraint.",
        insight: "The goal is to decouple your income from your time.",
        button: "Continue"
      },
      // Slide 3 - The Four Scaling Engines
      slide3: {
        title: "The Four Scaling Engines",
        intro: "There are four fundamental ways to multiply your value:",
        engines: [
          {
            name: "Skills Leverage",
            icon: "Zap",
            desc: "Increase the value of each hour through expertise",
            color: "yellow"
          },
          {
            name: "Systems Leverage",
            icon: "Building2",
            desc: "Build processes that produce value repeatedly",
            examples: "businesses, teams, franchises, automated services",
            color: "blue"
          },
          {
            name: "Capital Leverage",
            icon: "Coins",
            desc: "Use money to generate additional value",
            examples: "stocks, businesses, real estate, investments",
            color: "green"
          },
          {
            name: "Digital Leverage",
            icon: "Laptop",
            desc: "Create assets that can scale infinitely",
            examples: "software, courses, platforms, content",
            color: "purple"
          }
        ],
        button: "Continue"
      },
      // Slide 4 - The Value Scaling Ladder
      slide4: {
        title: "The Value Scaling Ladder",
        intro: "Your journey from trading time to building wealth:",
        levels: [
          { level: 1, name: "Selling Time", desc: "Hourly work, wages", icon: "Clock" },
          { level: 2, name: "Selling Expertise", desc: "Premium rates, consulting", icon: "GraduationCap" },
          { level: 3, name: "Building Systems", desc: "Teams, processes, businesses", icon: "Building2" },
          { level: 4, name: "Using Capital", desc: "Investments, ownership stakes", icon: "Coins" },
          { level: 5, name: "Creating Scalable Assets", desc: "Products, platforms, IP", icon: "Rocket" }
        ],
        insight: "Each level multiplies your potential income without proportionally increasing your time investment.",
        button: "Continue"
      },
      // Slide 5 - Examples of Scaling Paths
      slide5: {
        title: "Examples of Scaling Paths",
        intro: "See how others have scaled their value:",
        paths: [
          { from: "Freelancer", to: "Agency Owner", arrow: "→" },
          { from: "Consultant", to: "Course Creator", arrow: "→" },
          { from: "Employee", to: "Equity Partner", arrow: "→" },
          { from: "Chef", to: "Restaurant Brand", arrow: "→" },
          { from: "Teacher", to: "Online Academy", arrow: "→" }
        ],
        insight: "Every profession has scaling opportunities. The key is finding yours.",
        button: "Continue"
      },
      // Slide 6 - Identify Your Current Model
      slide6: {
        title: "Identify Your Current Model",
        intro: "Let's understand your starting point to suggest the best scaling strategies.",
        incomeLabel: "Current income model:",
        incomeOptions: [
          { id: 'salary', label: "Salary", desc: "Fixed monthly income" },
          { id: 'hourly', label: "Hourly work", desc: "Paid by the hour" },
          { id: 'project', label: "Project based", desc: "Per-project payments" },
          { id: 'business', label: "Business owner", desc: "Own a business" },
          { id: 'investments', label: "Investments", desc: "Income from capital" }
        ],
        industryLabel: "Your industry or profession:",
        industryPlaceholder: "e.g., Software, Finance, Healthcare...",
        skillsLabel: "Your key skills:",
        skillsPlaceholder: "e.g., Programming, Sales, Design...",
        interestsLabel: "What interests you most?",
        interestsPlaceholder: "e.g., Teaching, Building products...",
        button: "Generate My Scaling Paths"
      },
      // Slide 7 - Suggested Scaling Paths
      slide7: {
        title: "Your Scaling Paths",
        intro: "Based on your profile, here are 3 strategies to scale your value:",
        pathTemplates: {
          skills: {
            name: "Skill Specialization",
            desc: "Become a high-level expert and increase your market value.",
            actions: ["Develop deep expertise in a niche", "Build reputation and authority", "Command premium rates"]
          },
          systems: {
            name: "System Creation",
            desc: "Build a small business or team that multiplies your output.",
            actions: ["Document your processes", "Hire or partner with others", "Create repeatable systems"]
          },
          digital: {
            name: "Digital Leverage",
            desc: "Create scalable digital products or platforms.",
            actions: ["Package your knowledge into courses", "Build software tools", "Create content that compounds"]
          },
          capital: {
            name: "Capital Leverage",
            desc: "Use investments to grow your wealth passively.",
            actions: ["Invest in index funds or stocks", "Consider real estate", "Reinvest business profits"]
          }
        },
        selectPrompt: "Select a path to build your action plan:",
        button: "Continue"
      },
      // Slide 8 - Build Your Scaling Plan
      slide8: {
        title: "Build Your Scaling Plan",
        intro: "Define your personal scaling strategy:",
        goalLabel: "Your scaling goal:",
        goalPlaceholder: "e.g., Increase income without increasing working hours",
        strategyLabel: "Your chosen strategy:",
        actionsLabel: "Key actions to start:",
        actionOptions: [
          { id: 'expertise', label: "Develop new expertise" },
          { id: 'team', label: "Build a team" },
          { id: 'digital', label: "Create digital products" },
          { id: 'invest', label: "Invest capital" },
          { id: 'automate', label: "Automate processes" },
          { id: 'content', label: "Create content" },
          { id: 'network', label: "Build partnerships" }
        ],
        button: "See Expected Impact"
      },
      // Slide 9 - Expected Impact
      slide9: {
        title: "Expected Impact",
        intro: "By scaling your value, you unlock:",
        impacts: [
          { name: "Income Growth", desc: "Potential to multiply earnings", icon: "TrendingUp", color: "green" },
          { name: "Time Freedom", desc: "Earn more without working more hours", icon: "Clock", color: "blue" },
          { name: "Wealth Building", desc: "Create assets that compound over time", icon: "BarChart3", color: "purple" }
        ],
        insight: "Scaling is not about working harder. It's about working smarter.",
        button: "Continue"
      },
      // Slide 10 - Final Insight
      slide10: {
        title: "The Path Forward",
        message: "Wealth rarely comes from working more hours.",
        highlight: "It comes from scaling the value you create.",
        summary: "Your scaling plan is ready. Take action today.",
        saveButton: "Save My Scaling Plan",
        finishButton: "Finish"
      }
    },
    es: {
      // Slide 1 - Title
      slide1: {
        title: "Escala Tu Valor",
        message: "Si creas valor y capturas valor, puedes ganarte la vida.",
        highlight: "Si aprendes a escalar valor, construyes riqueza.",
        button: "Comenzar",
        time: "8-10 min"
      },
      // Slide 2 - The Scaling Problem
      slide2: {
        title: "El Problema de Escalar",
        intro: "La mayoría de las personas ganan dinero intercambiando tiempo por dinero:",
        formula: "Ingreso = Tiempo × Tarifa",
        problem: "Esto crea una limitación fundamental:",
        limitations: [
          "Solo hay 24 horas en un día",
          "Tu energía y enfoque son limitados",
          "El ingreso tiene un techo atado a tus horas"
        ],
        solution: "Escalar valor significa romper esta restricción.",
        insight: "El objetivo es desacoplar tu ingreso de tu tiempo.",
        button: "Continuar"
      },
      // Slide 3 - The Four Scaling Engines
      slide3: {
        title: "Los Cuatro Motores de Escala",
        intro: "Hay cuatro formas fundamentales de multiplicar tu valor:",
        engines: [
          {
            name: "Apalancamiento de Habilidades",
            icon: "Zap",
            desc: "Aumenta el valor de cada hora a través de la experiencia",
            color: "yellow"
          },
          {
            name: "Apalancamiento de Sistemas",
            icon: "Building2",
            desc: "Construye procesos que produzcan valor repetidamente",
            examples: "negocios, equipos, franquicias, servicios automatizados",
            color: "blue"
          },
          {
            name: "Apalancamiento de Capital",
            icon: "Coins",
            desc: "Usa dinero para generar valor adicional",
            examples: "acciones, negocios, bienes raíces, inversiones",
            color: "green"
          },
          {
            name: "Apalancamiento Digital",
            icon: "Laptop",
            desc: "Crea activos que pueden escalar infinitamente",
            examples: "software, cursos, plataformas, contenido",
            color: "purple"
          }
        ],
        button: "Continuar"
      },
      // Slide 4 - The Value Scaling Ladder
      slide4: {
        title: "La Escalera de Valor",
        intro: "Tu viaje desde vender tiempo hasta construir riqueza:",
        levels: [
          { level: 1, name: "Vendiendo Tiempo", desc: "Trabajo por hora, salarios", icon: "Clock" },
          { level: 2, name: "Vendiendo Experiencia", desc: "Tarifas premium, consultoría", icon: "GraduationCap" },
          { level: 3, name: "Construyendo Sistemas", desc: "Equipos, procesos, negocios", icon: "Building2" },
          { level: 4, name: "Usando Capital", desc: "Inversiones, participaciones", icon: "Coins" },
          { level: 5, name: "Creando Activos Escalables", desc: "Productos, plataformas, PI", icon: "Rocket" }
        ],
        insight: "Cada nivel multiplica tu ingreso potencial sin aumentar proporcionalmente tu inversión de tiempo.",
        button: "Continuar"
      },
      // Slide 5 - Examples of Scaling Paths
      slide5: {
        title: "Ejemplos de Caminos de Escala",
        intro: "Mira cómo otros han escalado su valor:",
        paths: [
          { from: "Freelancer", to: "Dueño de Agencia", arrow: "→" },
          { from: "Consultor", to: "Creador de Cursos", arrow: "→" },
          { from: "Empleado", to: "Socio con Participación", arrow: "→" },
          { from: "Chef", to: "Marca de Restaurantes", arrow: "→" },
          { from: "Maestro", to: "Academia Online", arrow: "→" }
        ],
        insight: "Cada profesión tiene oportunidades de escala. La clave es encontrar la tuya.",
        button: "Continuar"
      },
      // Slide 6 - Identify Your Current Model
      slide6: {
        title: "Identifica Tu Modelo Actual",
        intro: "Entendamos tu punto de partida para sugerir las mejores estrategias de escala.",
        incomeLabel: "Modelo de ingreso actual:",
        incomeOptions: [
          { id: 'salary', label: "Salario", desc: "Ingreso mensual fijo" },
          { id: 'hourly', label: "Trabajo por hora", desc: "Pago por hora" },
          { id: 'project', label: "Por proyecto", desc: "Pagos por proyecto" },
          { id: 'business', label: "Dueño de negocio", desc: "Tienes un negocio" },
          { id: 'investments', label: "Inversiones", desc: "Ingresos de capital" }
        ],
        industryLabel: "Tu industria o profesión:",
        industryPlaceholder: "ej., Software, Finanzas, Salud...",
        skillsLabel: "Tus habilidades clave:",
        skillsPlaceholder: "ej., Programación, Ventas, Diseño...",
        interestsLabel: "¿Qué te interesa más?",
        interestsPlaceholder: "ej., Enseñar, Crear productos...",
        button: "Generar Mis Caminos de Escala"
      },
      // Slide 7 - Suggested Scaling Paths
      slide7: {
        title: "Tus Caminos de Escala",
        intro: "Basado en tu perfil, aquí hay 3 estrategias para escalar tu valor:",
        pathTemplates: {
          skills: {
            name: "Especialización de Habilidades",
            desc: "Conviértete en un experto de alto nivel y aumenta tu valor de mercado.",
            actions: ["Desarrolla experiencia profunda en un nicho", "Construye reputación y autoridad", "Cobra tarifas premium"]
          },
          systems: {
            name: "Creación de Sistemas",
            desc: "Construye un pequeño negocio o equipo que multiplique tu producción.",
            actions: ["Documenta tus procesos", "Contrata o asóciate con otros", "Crea sistemas repetibles"]
          },
          digital: {
            name: "Apalancamiento Digital",
            desc: "Crea productos digitales o plataformas escalables.",
            actions: ["Empaqueta tu conocimiento en cursos", "Construye herramientas de software", "Crea contenido que se acumule"]
          },
          capital: {
            name: "Apalancamiento de Capital",
            desc: "Usa inversiones para hacer crecer tu riqueza pasivamente.",
            actions: ["Invierte en fondos indexados o acciones", "Considera bienes raíces", "Reinvierte ganancias del negocio"]
          }
        },
        selectPrompt: "Selecciona un camino para construir tu plan de acción:",
        button: "Continuar"
      },
      // Slide 8 - Build Your Scaling Plan
      slide8: {
        title: "Construye Tu Plan de Escala",
        intro: "Define tu estrategia personal de escala:",
        goalLabel: "Tu objetivo de escala:",
        goalPlaceholder: "ej., Aumentar ingresos sin aumentar horas de trabajo",
        strategyLabel: "Tu estrategia elegida:",
        actionsLabel: "Acciones clave para comenzar:",
        actionOptions: [
          { id: 'expertise', label: "Desarrollar nueva experiencia" },
          { id: 'team', label: "Construir un equipo" },
          { id: 'digital', label: "Crear productos digitales" },
          { id: 'invest', label: "Invertir capital" },
          { id: 'automate', label: "Automatizar procesos" },
          { id: 'content', label: "Crear contenido" },
          { id: 'network', label: "Construir alianzas" }
        ],
        button: "Ver Impacto Esperado"
      },
      // Slide 9 - Expected Impact
      slide9: {
        title: "Impacto Esperado",
        intro: "Al escalar tu valor, desbloqueas:",
        impacts: [
          { name: "Crecimiento de Ingresos", desc: "Potencial para multiplicar ganancias", icon: "TrendingUp", color: "green" },
          { name: "Libertad de Tiempo", desc: "Gana más sin trabajar más horas", icon: "Clock", color: "blue" },
          { name: "Construcción de Riqueza", desc: "Crea activos que se acumulan con el tiempo", icon: "BarChart3", color: "purple" }
        ],
        insight: "Escalar no se trata de trabajar más duro. Se trata de trabajar más inteligente.",
        button: "Continuar"
      },
      // Slide 10 - Final Insight
      slide10: {
        title: "El Camino Adelante",
        message: "La riqueza rara vez viene de trabajar más horas.",
        highlight: "Viene de escalar el valor que creas.",
        summary: "Tu plan de escala está listo. Toma acción hoy.",
        saveButton: "Guardar Mi Plan de Escala",
        finishButton: "Finalizar"
      }
    }
  };

  const c = content[lang];

  // Generate suggested paths based on user profile
  const generatePaths = () => {
    const paths = [];
    const templates = c.slide7.pathTemplates;
    
    // Always suggest skills path
    paths.push({ key: 'skills', ...templates.skills });
    
    // Suggest based on income model
    if (userProfile.incomeModel === 'salary' || userProfile.incomeModel === 'hourly') {
      paths.push({ key: 'systems', ...templates.systems });
      paths.push({ key: 'digital', ...templates.digital });
    } else if (userProfile.incomeModel === 'business') {
      paths.push({ key: 'digital', ...templates.digital });
      paths.push({ key: 'capital', ...templates.capital });
    } else if (userProfile.incomeModel === 'project') {
      paths.push({ key: 'systems', ...templates.systems });
      paths.push({ key: 'digital', ...templates.digital });
    } else {
      paths.push({ key: 'systems', ...templates.systems });
      paths.push({ key: 'capital', ...templates.capital });
    }
    
    setSuggestedPaths(paths.slice(0, 3));
    nextScreen();
  };

  const handleProfileChange = (field, value) => {
    setUserProfile(prev => ({ ...prev, [field]: value }));
  };

  const handlePlanChange = (field, value) => {
    setScalingPlan(prev => ({ ...prev, [field]: value }));
  };

  const toggleAction = (actionId) => {
    setScalingPlan(prev => ({
      ...prev,
      actions: prev.actions.includes(actionId)
        ? prev.actions.filter(a => a !== actionId)
        : [...prev.actions, actionId]
    }));
  };

  const selectStrategy = (path) => {
    setScalingPlan(prev => ({ ...prev, strategy: path }));
  };

  const savePlan = async () => {
    const plan = {
      ...scalingPlan,
      userProfile,
      suggestedPaths,
      createdAt: new Date().toISOString()
    };
    
    if (onSavePlan) {
      await onSavePlan(plan);
    }
  };

  const nextScreen = () => setCurrentScreen(prev => prev + 1);

  const handleFinish = () => {
    if (onClose) onClose();
  };

  // Animation variants
  const pageVariants = {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 }
  };

  // Icon component helper
  const IconComponent = ({ name, className }) => {
    const icons = {
      Zap, TrendingUp, Users, DollarSign, Clock, Building2, Laptop, Coins,
      Layers, GraduationCap, Briefcase, Rocket, Target, BarChart3
    };
    const Icon = icons[name];
    return Icon ? <Icon className={className} /> : null;
  };

  // Slide 1 - Title
  const renderSlide1 = () => (
    <motion.div 
      key="slide1"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
        <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center mb-4">
          <Rocket className="w-7 h-7 text-white" />
        </div>
        
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">{c.slide1.title}</h1>
        
        <div className="space-y-2 mb-4 max-w-sm">
          <p className="text-gray-300 text-sm">{c.slide1.message}</p>
          <p className="text-cyan-400 font-semibold text-sm">{c.slide1.highlight}</p>
        </div>
        
        <p className="text-gray-500 text-xs mt-2">{c.slide1.time}</p>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-bold rounded-xl hover:from-blue-400 hover:to-cyan-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide1.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 2 - The Scaling Problem
  const renderSlide2 = () => (
    <motion.div 
      key="slide2"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-hidden">
        <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center mb-3">
          <Clock className="w-5 h-5 text-red-500" />
        </div>
        
        <h1 className="text-lg md:text-xl font-bold text-white mb-2 text-center">{c.slide2.title}</h1>
        <p className="text-gray-400 text-xs mb-3 text-center max-w-sm">{c.slide2.intro}</p>
        
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2 mb-3">
          <p className="text-red-400 font-mono text-sm font-semibold">{c.slide2.formula}</p>
        </div>
        
        <p className="text-gray-300 text-xs mb-2 text-center">{c.slide2.problem}</p>
        
        <div className="space-y-1.5 mb-3 max-w-sm">
          {c.slide2.limitations.map((limit, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0"></div>
              <p className="text-gray-400 text-xs">{limit}</p>
            </div>
          ))}
        </div>
        
        <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 max-w-sm">
          <p className="text-cyan-400 text-sm font-medium text-center">{c.slide2.solution}</p>
          <p className="text-gray-400 text-xs text-center mt-1">{c.slide2.insight}</p>
        </div>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-bold rounded-xl hover:from-blue-400 hover:to-cyan-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide2.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 3 - The Four Scaling Engines
  const renderSlide3 = () => (
    <motion.div 
      key="slide3"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center px-4 py-3 overflow-y-auto">
        <h1 className="text-lg md:text-xl font-bold text-white mb-1 text-center">{c.slide3.title}</h1>
        <p className="text-gray-400 text-xs mb-3 text-center max-w-sm">{c.slide3.intro}</p>
        
        <div className="grid gap-2 w-full max-w-sm">
          {c.slide3.engines.map((engine, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-gray-800/50 border border-gray-700 rounded-lg p-2.5"
            >
              <div className="flex items-start gap-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-${engine.color}-500/20 text-${engine.color}-500`}>
                  <IconComponent name={engine.icon} className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-xs mb-0.5">{engine.name}</h3>
                  <p className="text-gray-400 text-xs leading-tight">{engine.desc}</p>
                  {engine.examples && (
                    <p className="text-gray-500 text-xs mt-0.5 italic">{engine.examples}</p>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-bold rounded-xl hover:from-blue-400 hover:to-cyan-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide3.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 4 - The Value Scaling Ladder
  const renderSlide4 = () => (
    <motion.div 
      key="slide4"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center px-4 py-3 overflow-y-auto">
        <h1 className="text-lg md:text-xl font-bold text-white mb-1 text-center">{c.slide4.title}</h1>
        <p className="text-gray-400 text-xs mb-3 text-center max-w-sm">{c.slide4.intro}</p>
        
        <div className="w-full max-w-sm space-y-1.5">
          {[...c.slide4.levels].reverse().map((level, i) => (
            <motion.div
              key={level.level}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`flex items-center gap-2 p-2 rounded-lg border ${
                level.level === 5 ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/50' :
                level.level === 4 ? 'bg-green-500/10 border-green-500/30' :
                level.level === 3 ? 'bg-blue-500/10 border-blue-500/30' :
                level.level === 2 ? 'bg-yellow-500/10 border-yellow-500/30' :
                'bg-gray-800/50 border-gray-700'
              }`}
            >
              <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${
                level.level === 5 ? 'bg-purple-500 text-white' :
                level.level === 4 ? 'bg-green-500 text-white' :
                level.level === 3 ? 'bg-blue-500 text-white' :
                level.level === 2 ? 'bg-yellow-500 text-black' :
                'bg-gray-600 text-white'
              }`}>
                {level.level}
              </div>
              <div className="flex-1">
                <p className="text-white text-xs font-medium">{level.name}</p>
                <p className="text-gray-400 text-xs">{level.desc}</p>
              </div>
              <IconComponent name={level.icon} className={`w-4 h-4 ${
                level.level === 5 ? 'text-purple-400' :
                level.level === 4 ? 'text-green-400' :
                level.level === 3 ? 'text-blue-400' :
                level.level === 2 ? 'text-yellow-400' :
                'text-gray-400'
              }`} />
            </motion.div>
          ))}
        </div>
        
        <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 mt-3 max-w-sm">
          <p className="text-cyan-400 text-xs text-center">{c.slide4.insight}</p>
        </div>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-bold rounded-xl hover:from-blue-400 hover:to-cyan-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide4.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 5 - Examples of Scaling Paths
  const renderSlide5 = () => (
    <motion.div 
      key="slide5"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-hidden">
        <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center mb-3">
          <ArrowUpRight className="w-5 h-5 text-green-500" />
        </div>
        
        <h1 className="text-lg md:text-xl font-bold text-white mb-2 text-center">{c.slide5.title}</h1>
        <p className="text-gray-400 text-xs mb-4 text-center max-w-sm">{c.slide5.intro}</p>
        
        <div className="space-y-2 w-full max-w-sm mb-4">
          {c.slide5.paths.map((path, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.1 }}
              className="bg-gray-800/50 border border-gray-700 rounded-lg p-2.5 flex items-center justify-between"
            >
              <span className="text-gray-400 text-xs">{path.from}</span>
              <span className="text-green-400 text-lg">{path.arrow}</span>
              <span className="text-white text-xs font-medium">{path.to}</span>
            </motion.div>
          ))}
        </div>
        
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2 max-w-sm">
          <p className="text-green-400 text-xs text-center">{c.slide5.insight}</p>
        </div>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-bold rounded-xl hover:from-blue-400 hover:to-cyan-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide5.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 6 - Identify Your Current Model (Interactive)
  const renderSlide6 = () => (
    <motion.div 
      key="slide6"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col px-4 py-3 overflow-y-auto">
        <h1 className="text-lg md:text-xl font-bold text-white mb-1 text-center">{c.slide6.title}</h1>
        <p className="text-gray-400 text-xs mb-3 text-center">{c.slide6.intro}</p>
        
        {/* Income Model Selection */}
        <p className="text-gray-300 text-xs mb-1.5">{c.slide6.incomeLabel}</p>
        <div className="grid grid-cols-2 gap-1.5 mb-3">
          {c.slide6.incomeOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => handleProfileChange('incomeModel', option.id)}
              className={`p-2 rounded-lg border text-left transition-all ${
                userProfile.incomeModel === option.id
                  ? 'bg-cyan-500/20 border-cyan-500'
                  : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
              }`}
            >
              <p className={`text-xs font-medium ${
                userProfile.incomeModel === option.id ? 'text-cyan-400' : 'text-white'
              }`}>{option.label}</p>
              <p className="text-gray-500 text-xs">{option.desc}</p>
            </button>
          ))}
        </div>
        
        {/* Text Inputs */}
        <div className="space-y-2">
          <div>
            <p className="text-gray-300 text-xs mb-1">{c.slide6.industryLabel}</p>
            <input
              type="text"
              value={userProfile.industry}
              onChange={(e) => handleProfileChange('industry', e.target.value)}
              placeholder={c.slide6.industryPlaceholder}
              className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
            />
          </div>
          <div>
            <p className="text-gray-300 text-xs mb-1">{c.slide6.skillsLabel}</p>
            <input
              type="text"
              value={userProfile.skills}
              onChange={(e) => handleProfileChange('skills', e.target.value)}
              placeholder={c.slide6.skillsPlaceholder}
              className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
            />
          </div>
          <div>
            <p className="text-gray-300 text-xs mb-1">{c.slide6.interestsLabel}</p>
            <input
              type="text"
              value={userProfile.interests}
              onChange={(e) => handleProfileChange('interests', e.target.value)}
              placeholder={c.slide6.interestsPlaceholder}
              className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
            />
          </div>
        </div>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={generatePaths}
          disabled={!userProfile.incomeModel}
          className={`px-5 py-2.5 font-bold rounded-xl transition-all flex items-center gap-2 text-sm ${
            userProfile.incomeModel
              ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white hover:from-blue-400 hover:to-cyan-500'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          {c.slide6.button}
        </button>
      </div>
    </motion.div>
  );

  // Slide 7 - Suggested Scaling Paths
  const renderSlide7 = () => (
    <motion.div 
      key="slide7"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col px-4 py-3 overflow-y-auto">
        <h1 className="text-lg md:text-xl font-bold text-white mb-1 text-center">{c.slide7.title}</h1>
        <p className="text-gray-400 text-xs mb-3 text-center">{c.slide7.intro}</p>
        
        <div className="space-y-2 mb-3">
          {suggestedPaths.map((path, i) => (
            <motion.button
              key={path.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.15 }}
              onClick={() => selectStrategy(path)}
              className={`w-full p-3 rounded-lg border text-left transition-all ${
                scalingPlan.strategy?.key === path.key
                  ? 'bg-cyan-500/20 border-cyan-500'
                  : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${
                  scalingPlan.strategy?.key === path.key ? 'bg-cyan-500 text-white' : 'bg-gray-600 text-white'
                }`}>
                  {i + 1}
                </div>
                <h3 className={`font-semibold text-sm ${
                  scalingPlan.strategy?.key === path.key ? 'text-cyan-400' : 'text-white'
                }`}>{path.name}</h3>
              </div>
              <p className="text-gray-400 text-xs mb-2 pl-8">{path.desc}</p>
              <div className="pl-8 space-y-0.5">
                {path.actions.map((action, j) => (
                  <div key={j} className="flex items-center gap-1.5">
                    <div className="w-1 h-1 rounded-full bg-gray-500"></div>
                    <p className="text-gray-500 text-xs">{action}</p>
                  </div>
                ))}
              </div>
            </motion.button>
          ))}
        </div>
        
        <p className="text-gray-400 text-xs text-center">{c.slide7.selectPrompt}</p>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          disabled={!scalingPlan.strategy}
          className={`px-5 py-2.5 font-bold rounded-xl transition-all flex items-center gap-2 text-sm ${
            scalingPlan.strategy
              ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white hover:from-blue-400 hover:to-cyan-500'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          {c.slide7.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 8 - Build Your Scaling Plan
  const renderSlide8 = () => (
    <motion.div 
      key="slide8"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col px-4 py-3 overflow-y-auto">
        <h1 className="text-lg md:text-xl font-bold text-white mb-1 text-center">{c.slide8.title}</h1>
        <p className="text-gray-400 text-xs mb-3 text-center">{c.slide8.intro}</p>
        
        {/* Goal Input */}
        <div className="mb-3">
          <p className="text-gray-300 text-xs mb-1">{c.slide8.goalLabel}</p>
          <input
            type="text"
            value={scalingPlan.goal}
            onChange={(e) => handlePlanChange('goal', e.target.value)}
            placeholder={c.slide8.goalPlaceholder}
            className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
          />
        </div>
        
        {/* Selected Strategy */}
        {scalingPlan.strategy && (
          <div className="mb-3">
            <p className="text-gray-300 text-xs mb-1">{c.slide8.strategyLabel}</p>
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2">
              <p className="text-cyan-400 text-sm font-medium">{scalingPlan.strategy.name}</p>
            </div>
          </div>
        )}
        
        {/* Actions Selection */}
        <div>
          <p className="text-gray-300 text-xs mb-1.5">{c.slide8.actionsLabel}</p>
          <div className="grid grid-cols-2 gap-1.5">
            {c.slide8.actionOptions.map((action) => (
              <button
                key={action.id}
                onClick={() => toggleAction(action.id)}
                className={`p-2 rounded-lg border text-left transition-all ${
                  scalingPlan.actions.includes(action.id)
                    ? 'bg-cyan-500/20 border-cyan-500'
                    : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                    scalingPlan.actions.includes(action.id)
                      ? 'bg-cyan-500 border-cyan-500'
                      : 'border-gray-600'
                  }`}>
                    {scalingPlan.actions.includes(action.id) && (
                      <CheckCircle2 className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <p className={`text-xs ${
                    scalingPlan.actions.includes(action.id) ? 'text-cyan-400' : 'text-white'
                  }`}>{action.label}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-bold rounded-xl hover:from-blue-400 hover:to-cyan-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide8.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 9 - Expected Impact
  const renderSlide9 = () => (
    <motion.div 
      key="slide9"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-hidden">
        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-3">
          <TrendingUp className="w-6 h-6 text-white" />
        </div>
        
        <h1 className="text-lg md:text-xl font-bold text-white mb-2 text-center">{c.slide9.title}</h1>
        <p className="text-gray-400 text-xs mb-4 text-center">{c.slide9.intro}</p>
        
        <div className="space-y-3 w-full max-w-sm mb-4">
          {c.slide9.impacts.map((impact, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.15 }}
              className={`bg-${impact.color}-500/10 border border-${impact.color}-500/30 rounded-lg p-3`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${impact.color}-500/20`}>
                  <IconComponent name={impact.icon} className={`w-5 h-5 text-${impact.color}-500`} />
                </div>
                <div>
                  <p className={`text-${impact.color}-400 font-semibold text-sm`}>{impact.name}</p>
                  <p className="text-gray-400 text-xs">{impact.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="bg-gray-800/30 rounded-lg p-2 max-w-sm">
          <p className="text-gray-300 text-xs text-center">{c.slide9.insight}</p>
        </div>
      </div>
      
      <div className="flex-shrink-0 flex justify-center pb-44">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-bold rounded-xl hover:from-blue-400 hover:to-cyan-500 transition-all flex items-center gap-2 text-sm"
        >
          {c.slide9.button}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  // Slide 10 - Final Insight
  const renderSlide10 = () => (
    <motion.div 
      key="slide10"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-hidden">
        <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
          <Rocket className="w-7 h-7 text-white" />
        </div>
        
        <h1 className="text-xl md:text-2xl font-bold text-white mb-3 text-center">{c.slide10.title}</h1>
        
        <div className="space-y-2 mb-4 max-w-sm text-center">
          <p className="text-gray-300 text-sm">{c.slide10.message}</p>
          <p className="text-cyan-400 font-semibold text-sm">{c.slide10.highlight}</p>
        </div>
        
        <p className="text-gray-400 text-xs mb-6 text-center max-w-sm">{c.slide10.summary}</p>
        
        {/* Plan Summary */}
        {scalingPlan.strategy && (
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 max-w-sm w-full mb-4">
            <p className="text-gray-400 text-xs mb-1">{lang === 'es' ? 'Tu estrategia:' : 'Your strategy:'}</p>
            <p className="text-cyan-400 text-sm font-medium">{scalingPlan.strategy.name}</p>
            {scalingPlan.goal && (
              <>
                <p className="text-gray-400 text-xs mt-2 mb-1">{lang === 'es' ? 'Tu objetivo:' : 'Your goal:'}</p>
                <p className="text-white text-xs">{scalingPlan.goal}</p>
              </>
            )}
          </div>
        )}
      </div>
      
      <div className="flex-shrink-0 flex flex-col items-center gap-2 pb-44">
        <button
          onClick={async () => {
            await savePlan();
            handleFinish();
          }}
          className="px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-xl hover:from-green-400 hover:to-emerald-500 transition-all flex items-center gap-2 text-sm"
        >
          <Save className="w-4 h-4" />
          {c.slide10.saveButton}
        </button>
        <button
          onClick={handleFinish}
          className="text-gray-400 text-xs hover:text-white transition-colors"
        >
          {c.slide10.finishButton}
        </button>
      </div>
    </motion.div>
  );

  // Screen renderer
  const screens = [
    renderSlide1,
    renderSlide2,
    renderSlide3,
    renderSlide4,
    renderSlide5,
    renderSlide6,
    renderSlide7,
    renderSlide8,
    renderSlide9,
    renderSlide10
  ];

  return (
    <div className="fixed inset-0 bg-black z-50 overflow-hidden">
      <div className="h-screen flex flex-col">
        {/* Progress bar */}
        <div className="flex-shrink-0 bg-black border-b border-gray-800 px-4 py-2 z-10">
          <div className="max-w-xl mx-auto">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-500 text-xs">
                {lang === 'es' ? 'Escala Tu Valor' : 'Scale Your Value'}
              </span>
              <span className="text-gray-500 text-xs">
                {currentScreen + 1} / {screens.length}
              </span>
            </div>
            <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-blue-500 to-cyan-500"
                initial={{ width: 0 }}
                animate={{ width: `${((currentScreen + 1) / screens.length) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        </div>
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-white transition-colors p-1.5 z-20"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-hidden">
            <div className="w-full max-w-3xl h-full mx-auto">
              <AnimatePresence mode="wait">
                {screens[currentScreen]()}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScaleValueModule;
